import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Collapse,
  TextField,
  MenuItem,
  Grid,
  ImageList,
  ImageListItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { useDataProvider } from "react-admin";
import CustomPagination from "../../Components/CustomPagination";

const formatDateTime = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const getStatusColor = (status) => {
  if (!status) return "default";
  switch (status.toLowerCase()) {
    case "new":
      return "primary";
    case "in_progress":
      return "warning";
    case "resolved":
      return "success";
    default:
      return "default";
  }
};

const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  return string
    .split(/[ _]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const isImageUrl = (url) => {
  if (!url) return false;
  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp"];
  const lowerUrl = url.toLowerCase();
  return imageExtensions.some((ext) => lowerUrl.includes(ext));
};

const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = [".mp4", ".mpeg", ".mov", ".avi", ".webm"];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some((ext) => lowerUrl.includes(ext));
};

const AttachmentPreviewDialog = ({ open, onClose, url }) => {
  const isImage = isImageUrl(url);
  const isVideo = isVideoUrl(url);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: "#1A1A1A",
          color: "#FFF",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#FFF",
          borderBottom: "1px solid rgba(255, 239, 153, 0.20)",
        }}
      >
        <Typography variant="h6">Attachment Preview</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "#FFF" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: "#1A1A1A" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 300,
          }}
        >
          {isImage ? (
            <img
              src={url}
              alt="Attachment"
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          ) : isVideo ? (
            <video
              controls
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
              }}
            >
              <source src={url} />
              Your browser does not support the video tag.
            </video>
          ) : (
            <Box sx={{ textAlign: "center" }}>
              <AttachFileIcon sx={{ fontSize: 60, color: "#FFD700" }} />
              <Typography variant="body2" sx={{ mt: 2, color: "rgba(255, 255, 255, 0.7)" }}>
                Preview not available for this file type
              </Typography>
              <Typography
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "block",
                  mt: 2,
                  color: "#FFD700",
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                    color: "#FFA000",
                  },
                }}
              >
                Open in new tab
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const TicketRow = ({ ticket }) => {
  const [open, setOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const hasAttachments =
    ticket.attachmentURL &&
    Array.isArray(ticket.attachmentURL) &&
    ticket.attachmentURL.length > 0;

  const handlePreviewOpen = (url) => {
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewUrl(null);
  };

  return (
    <>
      <TableRow
        sx={{
          "&:hover": {
            bgcolor: "rgba(255, 215, 0, 0.05)",
          },
          "& > *": { borderBottom: "unset", borderColor: "rgba(255, 239, 153, 0.10)" },
        }}
      >
        <TableCell sx={{ width: 50, color: "#FFF" }}>
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            sx={{
              transition: "transform 0.2s",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              color: "#FFD700",
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontSize: 13, color: "#FFF" }}>
          {formatDateTime(ticket.createdAt)}
        </TableCell>
        <TableCell sx={{ fontSize: 13, fontWeight: 500, color: "#FFD700" }}>
          {ticket.ticketId || ticket.id}
        </TableCell>
        <TableCell sx={{ fontSize: 13, color: "#FFF" }}>
          {capitalizeFirstLetter(ticket.category)}
        </TableCell>
        <TableCell>
          <Chip
            label={capitalizeFirstLetter(ticket.status)}
            color={getStatusColor(ticket.status)}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: 12,
            }}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0, borderColor: "rgba(255, 239, 153, 0.10)" }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: 600, color: "#FFD700" }}
                >
                  Description
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: "rgba(255, 215, 0, 0.05)",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "rgba(255, 239, 153, 0.20)",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
                    {ticket.description || "No description provided"}
                  </Typography>
                </Paper>
              </Box>

              {ticket.remarks && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 600, color: "#4CAF50" }}
                  >
                    Remarks
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: "rgba(76, 175, 80, 0.05)",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "rgba(76, 175, 80, 0.3)",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
                      {ticket.remarks}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {hasAttachments && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontWeight: 600, color: "#2196F3" }}
                  >
                    Attachments ({ticket.attachmentURL.length})
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: "rgba(33, 150, 243, 0.05)",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "rgba(33, 150, 243, 0.3)",
                    }}
                  >
                    <ImageList
                      sx={{
                        width: "100%",
                        maxHeight: 300,
                      }}
                      cols={3}
                      rowHeight={120}
                      gap={8}
                    >
                      {ticket.attachmentURL.map((url, index) => (
                        <ImageListItem
                          key={index}
                          sx={{
                            cursor: "pointer",
                            border: "1px solid",
                            borderColor: "rgba(255, 239, 153, 0.20)",
                            borderRadius: 1,
                            overflow: "hidden",
                            position: "relative",
                            bgcolor: "rgba(0, 0, 0, 0.3)",
                            "&:hover": {
                              opacity: 0.8,
                              boxShadow: "0 0 10px rgba(255, 215, 0, 0.3)",
                              borderColor: "#FFD700",
                            },
                          }}
                          onClick={() => handlePreviewOpen(url)}
                        >
                          {isImageUrl(url) ? (
                            <img
                              src={url}
                              alt={`Attachment ${index + 1}`}
                              loading="lazy"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                            />
                          ) : isVideoUrl(url) ? (
                            <Box
                              sx={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "grey.200",
                                position: "relative",
                              }}
                            >
                              <video
                                src={url}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <PlayCircleOutlineIcon
                                sx={{
                                  position: "absolute",
                                  fontSize: 40,
                                  color: "white",
                                  opacity: 0.9,
                                }}
                              />
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "rgba(0, 0, 0, 0.5)",
                              }}
                            >
                              <AttachFileIcon
                                sx={{ fontSize: 40, color: "#FFD700" }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ mt: 1, color: "rgba(255, 255, 255, 0.7)" }}
                              >
                                File {index + 1}
                              </Typography>
                            </Box>
                          )}
                        </ImageListItem>
                      ))}
                    </ImageList>
                  </Paper>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <AttachmentPreviewDialog
        open={previewOpen}
        onClose={handlePreviewClose}
        url={previewUrl}
      />
    </>
  );
};

const CATEGORIES = [
  { value: "", label: "All" },
  { value: "user_data", label: "User Data" },
  { value: "recharge", label: "Recharge" },
  { value: "redeem", label: "Redeem" },
  { value: "game_coins", label: "Game Coins" },
  { value: "daily_coins", label: "Daily Coins" },
  { value: "account", label: "Account" },
  { value: "profile", label: "Profile" },
  { value: "marketing", label: "Marketing" },
  { value: "reports", label: "Reports" },
  { value: "others", label: "Others" },
];

const STATUSES = [
  { value: "", label: "All Status" },
  { value: "new", label: "New" },
  { value: "in_progress", label: "In Progress" },
  { value: "resolved", label: "Resolved" },
];

const MyTicketsDialog = ({ open, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const dataProvider = useDataProvider();

  const fetchTickets = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      if (categoryFilter) filters.category = categoryFilter;

      const { data, total } = await dataProvider.getList("tickets", {
        pagination: { page, perPage },
        sort: { field: "createdAt", order: "DESC" },
        filter: filters,
      });

      setTickets(data);
      setTotal(total);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      setTickets([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [dataProvider, page, perPage, statusFilter, categoryFilter]);

  useEffect(() => {
    if (open) {
      fetchTickets();
    }
  }, [open, fetchTickets]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: "500px",
          bgcolor: "#1A1A1A",
          color: "#FFF",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          color: "#FFF",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          My Support Tickets
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#FFF",
            "&:hover": {
              transform: "rotate(90deg)",
              transition: "transform 0.3s",
              bgcolor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          borderColor: "rgba(255, 239, 153, 0.20)",
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Filter by Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    color: "#FFF",
                    "& fieldset": {
                      borderColor: "rgba(255, 239, 153, 0.20)",
                    },
                    "&:hover fieldset": {
                      borderColor: "#FFD700",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#FFD700",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255, 255, 255, 0.7)",
                    fontSize: "0.75em",
                    "&.Mui-focused": {
                      color: "#FFD700",
                    },
                  },
                  "& .MuiSelect-icon": {
                    color: "#FFD700",
                  },
                }}
              >
                {STATUSES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Filter by Category"
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(1);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    color: "#FFF",
                    "& fieldset": {
                      borderColor: "rgba(255, 239, 153, 0.20)",
                    },
                    "&:hover fieldset": {
                      borderColor: "#FFD700",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#FFD700",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255, 255, 255, 0.7)",
                    fontSize: "0.75em",
                    "&.Mui-focused": {
                      color: "#FFD700",
                    },
                  },
                  "& .MuiSelect-icon": {
                    color: "#FFD700",
                  },
                }}
              >
                {CATEGORIES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>

        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
            }}
          >
            <CircularProgress />
          </Box>
        ) : tickets.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
              gap: 2,
            }}
          >
            <Typography variant="h6" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
              No Tickets Found
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.5)" }}>
              You haven't created any support tickets yet.
            </Typography>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "rgba(255, 239, 153, 0.20)",
              borderRadius: 2,
              bgcolor: "transparent",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "rgba(255, 215, 0, 0.05)" }}>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#FFD700",
                      width: 50,
                    }}
                  >
                    Actions
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#FFD700",
                    }}
                  >
                    Date & Time
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#FFD700",
                    }}
                  >
                    Ticket ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#FFD700",
                    }}
                  >
                    Category
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#FFD700",
                    }}
                  >
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map((ticket) => (
                  <TicketRow key={ticket.id} ticket={ticket} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {total > 0 && (
          <Box sx={{ mt: 2 }}>
            <CustomPagination
              page={page}
              perPage={perPage}
              total={total}
              setPage={setPage}
              setPerPage={setPerPage}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MyTicketsDialog;
